import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Calendar, Award, ChevronRight } from 'lucide-react';
import api from '../lib/api';

const scoreColor = (s) => {
    if (s >= 8) return 'text-emerald-600 bg-emerald-50';
    if (s >= 5) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
};

export default function History() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/interviews/history');
                setSessions(res.data);
            } catch (_) {
                /* handled inline */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="space-y-6" data-testid="history-page">
            <header>
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#111827]">Interview History</h1>
                <p className="text-sm text-[#6B7280]">A log of every mock interview you&apos;ve taken.</p>
            </header>

            {loading ? (
                <div className="brand-card p-10 flex items-center justify-center gap-3 text-[#6B7280]">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
            ) : sessions.length === 0 ? (
                <div className="brand-card p-10 text-center">
                    <div className="text-lg font-bold text-[#111827]">No sessions yet</div>
                    <p className="mt-1 text-sm text-[#6B7280]">
                        Take your first mock interview to see it here.
                    </p>
                    <Link to="/home" className="brand-btn-primary mt-6 inline-flex">Start Interview</Link>
                </div>
            ) : (
                <div className="space-y-3">
                    {sessions.map((s) => {
                        const isAbandoned = s.status === 'abandoned';
                        const RowTag = isAbandoned ? 'div' : Link;
                        const rowProps = isAbandoned
                            ? {}
                            : { to: `/results/${s.id}` };
                        return (
                            <RowTag
                                {...rowProps}
                                key={s.id}
                                className={`brand-card p-5 flex items-center justify-between transition group ${
                                    isAbandoned
                                        ? 'opacity-75 cursor-default'
                                        : 'hover:border-[#1C3A94]/40 hover:shadow-md'
                                }`}
                                data-testid={`history-row-${s.id}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`flex h-12 w-12 items-center justify-center rounded-full font-extrabold ${scoreColor(s.overall_score || 0)}`}>
                                        {s.overall_score ?? '—'}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-[#111827]">
                                            {s.course}
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-3 text-xs text-[#6B7280]">
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar className="h-3 w-3" />
                                                {s.completed_at ? new Date(s.completed_at).toLocaleString() : new Date(s.created_at).toLocaleString()}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <Award className="h-3 w-3" />
                                                {s.status === 'completed'
                                                    ? 'Completed'
                                                    : s.status === 'abandoned'
                                                        ? 'Ended early'
                                                        : 'In progress'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {!isAbandoned && (
                                    <ChevronRight className="h-5 w-5 text-[#9CA3AF] group-hover:translate-x-0.5 transition" />
                                )}
                            </RowTag>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
